import { apiFetch, API_BASE, WEBAUTH_PUBLIC_URL } from '../api/client'
// Owned-country per-region statistics: data fetch, auth helpers, and the shared
// definitions of the heatmap layers driven by that data.

// Re-exported for the modules that already import them from here; the values
// themselves are resolved once in api/client.js.
export const PR_API_BASE = API_BASE
export { WEBAUTH_PUBLIC_URL }

/**
 * Login URL that returns to the current map page after authenticating.
 * Points at WebAuth's `/login` page (username/password + a Discord button),
 * not the Discord-only `/sso/login` shortcut — same flow Game-Dashboard relies on.
 */
export const buildLoginUrl = () =>
  `${WEBAUTH_PUBLIC_URL}/login?return_to=${encodeURIComponent(window.location.href)}`

export const buildLogoutUrl = () =>
  `${WEBAUTH_PUBLIC_URL}/logout?return_to=${encodeURIComponent(window.location.href)}`

/**
 * Fetch the logged-in player's per-region stats. Sends the shared SSO cookie via
 * credentials. Returns { country_id, structure_types, infra_types, regions }.
 * When logged out, country_id is null and regions is empty (not an error).
 */
export const fetchRegionStats = async () => {
  const resp = await apiFetch(`${PR_API_BASE}/map/my-region-stats`, {
    credentials: 'include',
  })
  if (!resp.ok) throw new Error(`region-stats HTTP ${resp.status}`)
  const payload = await resp.json()
  if (!payload || !payload.success) throw new Error('region-stats failed')
  return payload.data
}

// Layer keys for the owned-country heatmaps (mutually exclusive with base layers).
export const OWN_LAYER_KEYS = [
  'ownHousingCoverage',
  'ownComfort',
  'ownElecProduction',
  'ownElecMeeting',
  'ownStructures',
  'ownInfrastructure',
]

/**
 * Definitions for each owned-country heatmap layer. `prop` is the feature
 * property read by the heatmap; `dynamic` layers build their prop from a
 * type selector (`propPrefix` + selected type).
 */
export const OWN_LAYERS = [
  { key: 'ownHousingCoverage', label: 'Logement — couverture (%)', prop: 'stat_housing_coverage', ramp: 'housing_coverage' },
  { key: 'ownComfort', label: 'Logement — confort moyen', prop: 'stat_comfort', ramp: 'comfort' },
  { key: 'ownElecProduction', label: 'Électricité — production (MWh)', prop: 'stat_elec_production', ramp: 'elec_production' },
  { key: 'ownElecMeeting', label: 'Électricité — couverture conso. (%)', prop: 'stat_elec_meeting', ramp: 'elec_meeting' },
  // Structures use a type/specialisation/level filter → precomputed `stat_struct_sel`.
  { key: 'ownStructures', label: 'Structures', prop: 'stat_struct_sel', ramp: 'structures', structFilter: true },
  { key: 'ownInfrastructure', label: 'Infrastructures (km, par type)', propPrefix: 'stat_infra_', ramp: 'infrastructure', dynamic: 'infrastructure' },
]

/** Resolve the active owned-layer definition + concrete feature property. */
export const resolveOwnLayer = (layers, statType) => {
  const def = OWN_LAYERS.find((l) => layers[l.key])
  if (!def) return null
  let prop = def.prop
  if (def.dynamic) {
    const selected = statType?.[def.dynamic]
    if (!selected) return { ...def, prop: null }
    prop = `${def.propPrefix}${selected}`
  }
  return { ...def, prop }
}

/**
 * Count structures in a region's detail list matching a {type, specialisation,
 * level} filter. Empty/null filter fields mean "all".
 */
export const countStructures = (detail, filter) => {
  if (!Array.isArray(detail)) return 0
  return detail.reduce((sum, d) => {
    if (filter?.type && d.type !== filter.type) return sum
    if (filter?.specialisation && d.specialisation !== filter.specialisation) return sum
    if (filter?.level != null && filter.level !== '' && Number(d.level) !== Number(filter.level)) return sum
    return sum + (d.count || 0)
  }, 0)
}

/** Derive the available structure types / specialisations / levels from all regions. */
export const deriveStructOptions = (regions) => {
  const types = new Set()
  const specs = new Set()
  const levels = new Set()
  Object.values(regions || {}).forEach((r) => {
    (r.structures_detail || []).forEach((d) => {
      types.add(d.type)
      specs.add(d.specialisation)
      levels.add(Number(d.level))
    })
  })
  return {
    types: [...types].sort(),
    specialisations: [...specs].sort(),
    levels: [...levels].sort((a, b) => a - b),
  }
}
